import { PLANS, type PlanCode } from "@byfluent/domain";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  type BadgeColor,
  Card,
  LinkButton,
  Loading,
  PageHeader,
  Stat,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { brl, dateBR, dateTimeBR, monthLabel } from "../../lib/format";
import type { FinancePayment, PayoutStatus, PayoutSummary, Wallet } from "../../lib/types";

interface SellerFinanceData {
  month: string;
  thisMonth: { commissionCents: number; payingStudents: number; newClosings: number };
  history: { month: string; commissionCents: number; payments: FinancePayment[] }[];
  wallet: Wallet | null;
  payouts: PayoutSummary;
}

export const PAYOUT_STATUS: Record<PayoutStatus, { label: string; color: BadgeColor }> = {
  sent: { label: "Transferido", color: "green" },
  processing: { label: "Enviando", color: "blue" },
  failed: { label: "Em revisão", color: "gold" },
  unknown: { label: "Em revisão", color: "gold" },
};

/** Repasse mensal da ByFluent para a subconta (ADR 0006). */
export function PayoutsCard({ payouts }: { payouts: PayoutSummary }) {
  return (
    <Card title="Repasses">
      <div className="stack">
        <div className="stat">
          <div className="label">Próximo repasse, em {dateBR(payouts.next.date)}</div>
          <div className="value">{brl(payouts.next.amountCents)}</div>
          <div className="small muted">
            Pagamentos confirmados até o fim de {monthLabel(payouts.next.period)}.
            {payouts.laterCents > 0
              ? ` Já confirmado para o repasse seguinte: ${brl(payouts.laterCents)}.`
              : ""}
          </div>
        </div>
        <p className="small muted">
          A ByFluent transfere para a sua subconta na Asaas, no {payouts.businessDay}º dia útil de
          cada mês, o que foi pago no mês anterior, já descontados os estornos.
        </p>
        {payouts.history.map((p) => (
          <div
            key={p.id}
            className="row between"
            style={{ minHeight: 44, borderTop: "1px solid var(--line)" }}
          >
            <div className="small">
              <strong>{monthLabel(p.period)}</strong>
              <div className="muted">
                {p.sentAt ? `Transferido em ${dateBR(p.sentAt)}` : "Aguardando a Asaas"}
                {p.debitsCents > 0 ? ` · estornos descontados: ${brl(p.debitsCents)}` : ""}
              </div>
            </div>
            <div className="row">
              <span>{brl(p.amountCents)}</span>
              <Badge color={PAYOUT_STATUS[p.status].color}>{PAYOUT_STATUS[p.status].label}</Badge>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function WalletCard({ wallet }: { wallet: Wallet | null }) {
  return (
    <Card title="Carteira">
      {!wallet ? (
        <Alert kind="note">
          Sua subconta na Asaas ainda não foi criada. Fale com a administração.
        </Alert>
      ) : (
        <div className="stack">
          <div className="stat">
            <div className="label">Saldo na subconta Asaas</div>
            <div className="value">
              {wallet.balanceCents === null ? "Aguardando" : brl(wallet.balanceCents)}
            </div>
            {wallet.balanceAt ? (
              <div className="small muted">Atualizado em {dateTimeBR(wallet.balanceAt)}</div>
            ) : null}
          </div>
          {wallet.status !== "approved" ? (
            <Alert kind="warning">
              Subconta em análise na Asaas. Confira os documentos pendentes no app da Asaas.
            </Alert>
          ) : null}
          <LinkButton
            href={wallet.withdrawUrl}
            target="_blank"
            rel="noreferrer"
            variant="secondary"
          >
            Sacar na Asaas
          </LinkButton>
        </div>
      )}
    </Card>
  );
}

export function SellerFinance() {
  const q = useQuery({
    queryKey: ["seller-finance"],
    queryFn: () => api.get<SellerFinanceData>("/v1/sellers/me/finance"),
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  return (
    <>
      <PageHeader
        title="Financeiro"
        subtitle={`Comissão de 5% sobre os pagamentos confirmados, repassada no ${d.payouts.businessDay}º dia útil do mês seguinte. ${monthLabel(d.month)}.`}
      />
      <div className="grid cols-3">
        <Stat label="Comissão confirmada no mês" value={brl(d.thisMonth.commissionCents)} />
        <Stat label="Alunos pagantes" value={d.thisMonth.payingStudents} />
        <Stat label="Novos fechamentos" value={d.thisMonth.newClosings} />
      </div>
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <Card title="Histórico">
          {d.history.length === 0 ? (
            <p className="muted">Ainda não há pagamentos confirmados.</p>
          ) : null}
          {d.history.map((m) => (
            <details key={m.month} open={m.month === d.month} style={{ marginBottom: 12 }}>
              <summary className="row between" style={{ cursor: "pointer", minHeight: 44 }}>
                <strong>{monthLabel(m.month)}</strong>
                <span>{brl(m.commissionCents)}</span>
              </summary>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Aluno</th>
                      <th>Plano</th>
                      <th className="num">Bruto</th>
                      <th className="num">Comissão</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.payments.map((p) => (
                      <tr key={p.paymentId}>
                        <td>{p.student}</td>
                        <td>{PLANS[p.planCode as PlanCode]?.name ?? p.planCode}</td>
                        <td className="num">{brl(p.grossCents)}</td>
                        <td className="num">{brl(p.commissionCents)}</td>
                        <td>{dateBR(p.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </Card>
        <div className="stack">
          <PayoutsCard payouts={d.payouts} />
          <WalletCard wallet={d.wallet} />
        </div>
      </div>
    </>
  );
}
